import { useRef, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { cn } from "../../utils/cn"
import { getTypewriterRevealMotion } from "./typewriterRevealLogic.mjs"

// NOTE: the un-smoothed `TypewriterEffect` export used to live here too, but
// it had zero importers anywhere in the app (only `TypewriterEffectSmooth`
// is used, on the landing page hero) — removed as dead code.

export const TypewriterEffectSmooth = ({
  words,
  className,
  // Lets the caller render the visible title as a real heading (e.g. `h1`)
  // without changing this component's markup/classes for every other
  // caller. Defaults to `div`, matching the original output exactly.
  as: HeadingTag = "div"
}) => {
  const prefersReducedMotion = useReducedMotion()
  const containerRef = useRef(null)

  // Tracks whether the width-reveal sweep has finished. Once it has, we drop
  // the `overflow-hidden`/fixed-width wrapper entirely instead of trusting
  // the animated pixel value framer-motion measured for "fit-content" — that
  // measurement can land a hair short (subpixel rounding, font metrics not
  // settled yet), which used to clip the final glyph of the last word (the
  // "m" in "System") under the blinking cursor. Reduced-motion users skip
  // the sweep altogether and start already "revealed".
  const [revealed, setRevealed] = useState(prefersReducedMotion)

  const wordsArray = words.map((word) => {
    return {
      ...word,
      text: word.text.split("")
    }
  })

  const renderWords = () => {
    return (
      <div>
        {wordsArray.map((word, idx) => {
          return (
            <div key={`word-${idx}`} className="inline-block">
              {word.text.map((char, index) => (
                <span
                  key={`char-${index}`}
                  className={cn(`text-black dark:text-white`, word.className)}
                >
                  {char}
                </span>
              ))}
              &nbsp;
            </div>
          )
        })}
      </div>
    )
  }

  // ROOT CAUSE FIX (see typewriterRevealLogic.mjs): this wrapper used to
  // pass `whileInView` with no `viewport` prop, so framer-motion's default
  // (`{ once: false }`) replayed the width sweep — a layout-property
  // (reflow) animation — every time the hero scrolled back into view.
  // That was the literal jitter the client saw scrolling down-then-up
  // past the hero: the headline re-collapsed and re-expanded mid-scroll.
  // `getTypewriterRevealMotion` always returns `viewport: { once: true }`,
  // so the sweep can fire at most once per mount, and collapses to a
  // fully static (no-animation) render once `revealed` latches true or
  // for reduced-motion users from the start.
  const revealMotion = getTypewriterRevealMotion({ revealed, prefersReducedMotion })

  // No trailing cursor: the effect is a width-reveal sweep, not char-by-char
  // typing, so a blinking bar tracked nothing — and as a flex sibling it wrapped
  // onto its own centered row when the title filled the line, manufacturing a
  // phantom gap between the headline and whatever sits beneath it (the hero
  // IEWRS lockup). Dropping it makes the reveal clean and the spacing honest.
  return (
    <div className={cn("flex items-center justify-center", className)}>
      <motion.div
        ref={containerRef}
        className={cn("pb-1", revealed ? "overflow-visible" : "overflow-hidden")}
        initial={revealMotion.initial}
        whileInView={revealMotion.whileInView}
        viewport={revealMotion.viewport}
        transition={revealMotion.transition}
        onAnimationComplete={() => {
          setRevealed(true)
          // Belt-and-braces: force the final DOM state to a natural, unclipped
          // width regardless of whatever pixel value the "fit-content" tween
          // measured mid-flight. This is the guarantee that the full word
          // always renders, independent of any animation-library rounding.
          if (containerRef.current) {
            containerRef.current.style.width = "auto"
            containerRef.current.style.overflow = "visible"
          }
        }}
      >
        <HeadingTag
          className="pr-1 text-xs sm:text-base md:text-xl lg:text-3xl xl:text-5xl font-bold"
          style={{
            whiteSpace: "nowrap"
          }}
        >
          {renderWords()}{" "}
        </HeadingTag>{" "}
      </motion.div>
    </div>
  )
}
